import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { JetSportBox } from "../../../components/BoxView/JetSportBox";
import { BoxHeader2 } from "../../../components/BoxView/BoxHeader2";
import { GoogleAnalyticsReportDto } from "../../../../data/models/responses/StatisticsResponses";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

interface Props {
  overview: GoogleAnalyticsReportDto | undefined;
  topPages: GoogleAnalyticsReportDto | undefined;
  devices: GoogleAnalyticsReportDto | undefined;
  isLoading: boolean;
}

const DEVICE_COLORS = ["#1B76DF", "#7BBB58", "#E7B400", "#9333EA", "#94a3b8"];

export function GoogleAnalyticsPanel({
  overview,
  topPages,
  devices,
  isLoading,
}: Props) {
  // Helpful empty state if credentials missing or any of the reports erred.
  const reports = [overview, topPages, devices];
  const notConfigured = reports.some((r) => r?.notConfigured);
  const errorMessage = reports.find((r) => r?.errorMessage)?.errorMessage;

  if (notConfigured) {
    return (
      <JetSportBox
        className="opacity-0 animate-fadeIn col-span-12"
        style={{ animationDelay: "640ms", animationFillMode: "forwards" }}
      >
        <div className="px-8 py-12 flex flex-col items-center text-center gap-3">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-status-pending-1 text-2xl"
          />
          <h3 className="text-base font-semibold text-black">
            Google Analytics not configured
          </h3>
          <p className="text-sm text-gray-600 max-w-md">
            Set <code className="text-xs bg-gray-100 px-1 rounded">GoogleAnalytics:PropertyId</code>{" "}
            and{" "}
            <code className="text-xs bg-gray-100 px-1 rounded">GoogleAnalytics:ServiceAccountKeyJson</code>{" "}
            in the API settings to enable this section.
          </p>
        </div>
      </JetSportBox>
    );
  }

  // Pull metric values from the overview report by metric name.
  function getOverviewMetric(name: string): string {
    if (!overview || overview.rows.length === 0) return "—";
    const idx = overview.metrics.findIndex((m) => m === name);
    if (idx === -1) return "—";
    const raw = overview.rows[0]?.metricValues[idx] ?? "0";
    return raw;
  }

  function fmtInt(raw: string): string {
    const n = Number(raw);
    return Number.isFinite(n) ? Math.round(n).toLocaleString() : raw;
  }

  function fmtPct(raw: string): string {
    const n = Number(raw);
    return Number.isFinite(n) ? `${(n * 100).toFixed(1)}%` : raw;
  }

  const kpis = [
    { label: "Active users", value: fmtInt(getOverviewMetric("activeUsers")) },
    { label: "Sessions", value: fmtInt(getOverviewMetric("sessions")) },
    { label: "Page views", value: fmtInt(getOverviewMetric("screenPageViews")) },
    { label: "Bounce rate", value: fmtPct(getOverviewMetric("bounceRate")) },
  ];

  // Top pages: dimension = pagePath, metric[0] = screenPageViews
  const pageRows = topPages?.rows.slice(0, 8) ?? [];

  // Devices: dimension = deviceCategory, metric[0] = activeUsers
  const deviceData =
    devices?.rows.map((r, i) => ({
      name: r.dimensionValues[0] ?? "unknown",
      value: Number(r.metricValues[0] ?? 0),
      color: DEVICE_COLORS[i % DEVICE_COLORS.length],
    })) ?? [];

  return (
    <>
      {/* Section header */}
      <div
        className="col-span-12 flex items-baseline justify-between opacity-0 animate-fadeIn"
        style={{ animationDelay: "640ms", animationFillMode: "forwards" }}
      >
        <div className="flex flex-col">
          <span className="text-xxs uppercase tracking-[0.22em] text-gray-600 font-semibold">
            Google Analytics
          </span>
          <h2 className="text-xl font-bold text-black mt-1">Web traffic</h2>
        </div>
        {errorMessage && (
          <span className="text-xs text-status-failed-1">{errorMessage}</span>
        )}
      </div>

      {/* GA KPI row */}
      {kpis.map((k, i) => (
        <JetSportBox
          key={k.label}
          className="col-span-6 lg:col-span-3 opacity-0 animate-fadeIn"
          style={{
            animationDelay: `${720 + i * 80}ms`,
            animationFillMode: "forwards",
          }}
        >
          <div className="px-6 py-5 flex flex-col gap-1">
            <span className="text-xxs uppercase tracking-[0.22em] text-gray-600 font-semibold">
              {k.label}
            </span>
            <span className="text-3xl font-bold tracking-tight text-black tabular-nums mt-1">
              {isLoading ? "—" : k.value}
            </span>
          </div>
        </JetSportBox>
      ))}

      {/* Top pages */}
      <JetSportBox
        className="col-span-12 lg:col-span-8 opacity-0 animate-fadeIn"
        style={{ animationDelay: "1040ms", animationFillMode: "forwards" }}
      >
        <BoxHeader2 className="!py-5 !px-6 border-b border-borderGray">
          <div className="flex flex-col">
            <span className="text-xxs uppercase tracking-[0.22em] text-gray-600 font-semibold">
              Engagement
            </span>
            <span className="text-base font-semibold text-black mt-0.5">
              Top pages
            </span>
          </div>
        </BoxHeader2>
        <div className="px-6 py-4">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="h-8 bg-gray-100 rounded animate-pulse"
                />
              ))}
            </div>
          ) : pageRows.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-600">
              No page data for the selected period.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xxs uppercase tracking-wider text-gray-600 font-semibold">
                  <th className="text-left py-2 font-semibold">Page</th>
                  <th className="text-right py-2 font-semibold">Views</th>
                  <th className="text-right py-2 font-semibold">Users</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borderGray">
                {pageRows.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-100/60 transition-colors">
                    <td className="py-2.5 pr-4 truncate max-w-0 text-black font-medium">
                      {row.dimensionValues[0]}
                    </td>
                    <td className="py-2.5 text-right tabular-nums">
                      {Number(row.metricValues[0] ?? 0).toLocaleString()}
                    </td>
                    <td className="py-2.5 text-right tabular-nums text-gray-600">
                      {Number(row.metricValues[1] ?? 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </JetSportBox>

      {/* Devices pie */}
      <JetSportBox
        className="col-span-12 lg:col-span-4 opacity-0 animate-fadeIn"
        style={{ animationDelay: "1120ms", animationFillMode: "forwards" }}
      >
        <BoxHeader2 className="!py-5 !px-6 border-b border-borderGray">
          <div className="flex flex-col">
            <span className="text-xxs uppercase tracking-[0.22em] text-gray-600 font-semibold">
              Devices
            </span>
            <span className="text-base font-semibold text-black mt-0.5">
              By category
            </span>
          </div>
        </BoxHeader2>
        <div className="px-6 py-4">
          {isLoading || deviceData.length === 0 ? (
            <div className="h-[200px] bg-gray-100/40 animate-pulse rounded-md" />
          ) : (
            <>
              <div className="h-[180px]">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={deviceData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={75}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {deviceData.map((d) => (
                        <Cell key={d.name} fill={d.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: "1px solid #ECEEF9",
                        fontSize: 12,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-1.5 mt-3">
                {deviceData.map((d) => (
                  <li
                    key={d.name}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="flex items-center gap-2 capitalize text-gray-600">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: d.color }}
                      />
                      {d.name}
                    </span>
                    <span className="tabular-nums font-semibold text-black">
                      {d.value.toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </JetSportBox>
    </>
  );
}
